import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LocalizedLink } from "@/i18n/routing";

const CONSENT_KEY = "barefoot-cookie-consent";

function applyConsent(granted) {
    if (typeof window.gtag === "function") {
        window.gtag("consent", "update", {
            ad_storage: granted ? "granted" : "denied",
            ad_user_data: granted ? "granted" : "denied",
            ad_personalization: granted ? "granted" : "denied",
            analytics_storage: granted ? "granted" : "denied",
        });
    }
    try {
        if (window.posthog) {
            if (granted) window.posthog.opt_in_capturing();
            else window.posthog.opt_out_capturing();
        }
    } catch (e) {
        /* posthog not ready — ignored */
    }
}

export const CookieConsent = () => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        let stored = null;
        try {
            stored = localStorage.getItem(CONSENT_KEY);
        } catch (e) {
            /* storage blocked */
        }
        if (stored === "granted") {
            applyConsent(true);
        } else if (stored === "denied") {
            applyConsent(false);
        } else {
            setVisible(true);
        }
    }, []);

    useEffect(() => {
        const reopen = () => setVisible(true);
        window.addEventListener("open-cookie-preferences", reopen);
        return () => window.removeEventListener("open-cookie-preferences", reopen);
    }, []);

    const choose = (granted) => {
        try {
            localStorage.setItem(CONSENT_KEY, granted ? "granted" : "denied");
        } catch (e) {
            /* storage blocked */
        }
        applyConsent(granted);
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            data-testid="cookie-consent-banner"
            role="dialog"
            aria-label={t("cookieBanner.title")}
            className="fixed bottom-0 inset-x-0 z-[120] bg-[#fdfbf7]/95 backdrop-blur-md border-t border-[#e5d9bf] shadow-[0_-8px_30px_rgba(26,26,24,0.08)]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                <p className="flex-1 text-[13px] leading-[1.7] text-[#5c5a55] font-light">
                    {t("cookieBanner.text")}{" "}
                    <LocalizedLink
                        to="/cookie-policy"
                        data-testid="cookie-consent-policy-link"
                        className="text-[#1a1a18] underline underline-offset-2 hover:text-[#7a4a3a] transition-colors"
                    >
                        {t("cookieBanner.policyLink")}
                    </LocalizedLink>
                </p>
                <div className="flex items-center gap-3 shrink-0">
                    <button
                        type="button"
                        data-testid="cookie-consent-refuse"
                        onClick={() => choose(false)}
                        className="px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-light text-[#1a1a18] border border-[#cfc6b3] hover:border-[#1a1a18] transition-colors"
                    >
                        {t("cookieBanner.refuse")}
                    </button>
                    <button
                        type="button"
                        data-testid="cookie-consent-accept"
                        onClick={() => choose(true)}
                        className="px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-light text-white bg-[#1a1a18] hover:bg-[#7a4a3a] transition-colors"
                    >
                        {t("cookieBanner.accept")}
                    </button>
                </div>
            </div>
        </div>
    );
};
